import { type ReactNode, useRef } from "react";

import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import MediaDisplay from "@/common/MediaDisplay";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export type CustomizedSliderMediaType = "image" | "video" | "pdf";

export interface CustomizedSliderSlide {
  id?: string | number;
  src: string;
  mediaType: CustomizedSliderMediaType;
  footer?: ReactNode;
}

export interface MaxSlidesPerView {
  mobile?: number;
  desktop?: number;
}

interface CustomizedSliderProps {
  slides: CustomizedSliderSlide[];
  customVideoErrorMessage?: string;
  handleSlideClick: (index: number) => void;
  handleVideoErrorClick?: () => void;
  maxSlidesPerView?: MaxSlidesPerView;
}

export default function CustomizedSlider({
  slides,
  customVideoErrorMessage,
  handleSlideClick,
  handleVideoErrorClick,
  maxSlidesPerView,
}: CustomizedSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the parent/container element
  const swiperRef = useRef<any>(null); // Ref for the Swiper instance
  const desktopMax = maxSlidesPerView?.desktop ?? 3;
  const mobileMax = maxSlidesPerView?.mobile ?? 1;
  const shouldLoopDesktop = slides.length > desktopMax;
  const shouldLoopMobile = slides.length > mobileMax;

  const handleMediaLoad = (
    e: any,
    mediaType: CustomizedSliderMediaType,
    shouldFillHeight: boolean,
  ) => {
    const target = e.target as HTMLImageElement;
    const { naturalHeight, naturalWidth } = target;

    if (mediaType === "video") {
      if (naturalHeight > naturalWidth) {
        target.style.height = "100%";
        target.style.width = "auto";
      } else {
        target.style.width = "100%";
        target.style.height = "auto";
      }
      target.style.objectFit = "contain";
      return;
    }

    target.style.width = "100%";
    if (shouldFillHeight) {
      target.style.height = "100%";
    }
    target.style.objectFit = "contain";
  };

  return (
    <div ref={containerRef}>
      <Swiper
        ref={swiperRef} // Pass the ref here
        navigation={true}
        observeParents={true}
        breakpointsBase="container"
        loop={shouldLoopDesktop}
        breakpoints={{
          320: {
            slidesPerView: Math.min(2, desktopMax),
          },
          480: {
            slidesPerView: desktopMax,
          },
        }}
        spaceBetween={10}
        centeredSlides={slides.length === 1}
        pagination={{ clickable: true, type: "progressbar" }}
        modules={[Pagination, Navigation]}
        className="desktop-swiper d-none d-md-block"
      >
        {slides.map((slide, index) => (
          <SwiperSlide
            key={slide.id ?? `${slide.src}-${index}`}
            className="h-100 d-flex justify-content-center align-items-center"
            onClick={() => handleSlideClick(index)} // On slide click, show full-screen
          >
            <div className="pt-2">
              <MediaDisplay
                src={slide.src}
                mediaType={slide.mediaType}
                onClick={() => handleSlideClick(index)}
                onLoad={(e) => handleMediaLoad(e, slide.mediaType, false)}
                customVideoErrorMessage={customVideoErrorMessage}
                handleVideoErrorClick={handleVideoErrorClick}
              />
              {slide.footer}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {/* Mobile Swiper */}
      <Swiper
        navigation={true}
        slidesPerView={mobileMax}
        spaceBetween={20}
        pagination={{ clickable: true, type: "progressbar" }}
        centeredSlides={true}
        loop={shouldLoopMobile}
        modules={[Pagination, Navigation]}
        className="d-md-none"
        style={
          {
            // width: "100vw",
          }
        }
      >
        {slides.map((slide, index) => (
          <SwiperSlide
            key={slide.id ?? `${slide.src}-${index}`}
            className="h-100 d-flex justify-content-center align-items-center"
            onClick={() => handleSlideClick(index)}
          >
            <div
              style={{
                overflow: "hidden",
              }}
            >
              <MediaDisplay
                src={slide.src}
                mediaType={slide.mediaType}
                onClick={() => handleSlideClick(index)}
                onLoad={(e) => handleMediaLoad(e, slide.mediaType, true)}
                customVideoErrorMessage={customVideoErrorMessage}
                handleVideoErrorClick={handleVideoErrorClick}
              />
              {slide.footer}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
